/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { AllergyIntoleranceBundle, CarePlanBundle, CareTeamBundle, ConditionBundle, DiagnosticReportBundle, ImmunizationBundle, ExplanationOfBenefitBundle, MedicationRequestBundle, ObservationBundle, OrganizationAffiliationBundle, ProcedureBundle, GoalBundle } from '../types/FHIRTypes/Bundle'
import { CodeableConcept } from '../types/FHIRTypes/CodeableConcept'
import { Coding } from '../types/FHIRTypes/Coding'
import { Extension } from '../types/FHIRTypes/Extension'
import { DateTime } from '../types/FHIRTypes/PrimitiveFhirTypes'
import { Reference } from '../types/FHIRTypes/Reference'

export const isCoding = (data: any): data is Coding => {
    if (typeof data !== 'object' || data === null) {
        return false
    }
    if (data.extension && Array.isArray(data.extension)) {
        return false
    }
    const hasValidSystem = data.system === undefined || data.system === null || typeof data.system === 'string'
    const hasValidCode = data.code === undefined || data.code === null || typeof data.code === 'string'
    const hasValidDisplay = data.display === undefined || data.display === null || typeof data.display === 'string'

    return hasValidSystem && hasValidCode && hasValidDisplay
}

export const isExtension = (data: any): data is Extension => {
    if (typeof data !== 'object' || data === null) {
        return false
    }

    if (typeof data.url !== 'string') {
        return false
    }

    const optionalStringProps = [
        'valueBase64Binary', 'valueCanonical', 'valueCode', 'valueDate', 'valueDateTime',
        'valueId', 'valueInstant', 'valueMarkdown', 'valueOid', 'valueString', 'valueTime',
        'valueUri', 'valueUrl', 'valueUuid', 'valueSignature'
    ]

    const optionalNumberProps = [
        'valueDecimal', 'valueInteger', 'valuePositiveInt', 'valueUnsignedInt', 'valueAge',
        'valueCount', 'valueDistance', 'valueDuration'
    ]

    const optionalBooleanProps = ['valueBoolean']

    const optionalObjectProps = [
        'valueAddress', 'valueAnnotation', 'valueAttachment', 'valueCodeableConcept', 'valueCoding',
        'valueContactPoint', 'valueHumanName', 'valueIdentifier', 'valueMoney', 'valuePeriod',
        'valueQuantity', 'valueRange', 'valueRatio', 'valueReference', 'valueSampledData',
        'valueTiming', 'valueContactDetail', 'valueContributor', 'valueDataRequirement',
        'valueExpression', 'valueDosage', 'valueMeta'
    ]

    const checkOptionalProps = (props: string[], typeCheck: (value: any) => boolean) => {
        return props.every(prop => data[prop] === undefined || typeCheck(data[prop]))
    }

    const isString = (value: any) => typeof value === 'string'
    const isNumber = (value: any) => typeof value === 'number'
    const isBoolean = (value: any) => typeof value === 'boolean'
    const isObject = (value: any) => typeof value === 'object' && value !== null

    return (
        checkOptionalProps(optionalStringProps, isString) &&
        checkOptionalProps(optionalNumberProps, isNumber) &&
        checkOptionalProps(optionalBooleanProps, isBoolean) &&
        checkOptionalProps(optionalObjectProps, isObject)
    )

}

export const isCodeableConcept = (data: any): data is CodeableConcept => {
    if (typeof data !== 'object' || data === null) {
        return false
    }
    if (data.extension && Array.isArray(data.extension)) {
        return false
    }

    const hasValidText = data.text === undefined || data.text === null || typeof data.text === 'string'
    const hasValidCoding = data.coding === undefined || (Array.isArray(data.coding) && data.coding.every(isCoding))

    return hasValidText && hasValidCoding
}

export const isDateTime = (data: any): data is DateTime => {
    if (typeof data !== 'string') {
        return false
    }
    return /^([0-9]([0-9]([0-9][1-9]|[1-9]0)|[1-9]00)|[1-9]000)(-(0[1-9]|1[0-2])(-(0[1-9]|[1-2][0-9]|3[0-1])(T([01][0-9]|2[0-3]):[0-5][0-9]:([0-5][0-9]|60)(\.[0-9]+)?(Z|(\+|-)((0[0-9]|1[0-3]):[0-5][0-9]|14:00)))?)?)?$/.test(data)
}

export const isReference = (data: any): data is Reference => {
    if (typeof data !== 'object' || data === null) {
        return false
    }
    // At least one of reference, identifier and display SHALL be present
    const hasValidReference = data.reference != undefined || data.reference != null || typeof data.reference === 'string'
    const hasValidIdentifier = data.identifier != undefined || data.identifier != null || typeof data.identifier === 'object'
    const hasValidDisplay = data.display != undefined || data.display != null || typeof data.display === 'string'

    return hasValidReference || hasValidIdentifier || hasValidDisplay
}

export const isAllergyIntoleranceBundle = (
    bundle: unknown
): bundle is AllergyIntoleranceBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'AllergyIntolerance'
        }
    }

    return false
}

export const isCarePlanBundle = (
    bundle: unknown
): bundle is CarePlanBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'CarePlan'
        }
    }

    return false
}

export const isCareTeamBundle = (
    bundle: unknown
): bundle is CareTeamBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry
        if ((bundle as any).total === 0) {
            return true
        }
        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'CareTeam'
        }
    }

    return false
}

export const isConditionBundle = (
    bundle: unknown
): bundle is ConditionBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'Condition'
        }
    }

    return false
}

export const isDiagnosticReportBundle = (
    bundle: unknown
): bundle is DiagnosticReportBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle'
    ) {
        const entries = (bundle as any).entry
        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            //first resource returned is practitioner on DEMO DEV
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'DiagnosticReport'
        }
    }
    return false
}


export const isImmunizationBundle = (
    bundle: unknown
): bundle is ImmunizationBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry
        if ((bundle as any).total === 0) {
            return true
        }
        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'Immunization'
        }
    }

    return false
}

export const isObservationBundle = (
    bundle: unknown
): bundle is ObservationBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'Observation'
        }
    }

    return false
}

export const isExplanationOfBenefitBundle = (
    bundle: unknown
): bundle is ExplanationOfBenefitBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry
        if ((bundle as any).total === 0) {
            return true
        }
        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'ExplanationOfBenefit'
        }
    }

    return false
}

export const isMedicationRequestBundle = (
    bundle: unknown
): bundle is MedicationRequestBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'MedicationRequest'
        }
    }

    return false
}

export const isProcedureBundle = (
    bundle: unknown
): bundle is ProcedureBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'Procedure'
        }
    }

    return false
}


export const isOrganizationAffiliationBundle = (
    bundle: unknown
): bundle is OrganizationAffiliationBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry

        if ((bundle as any).total === 0) {
            return true
        }

        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'OrganizationAffiliation'
        }
    }

    return false
}

export const isGoalBundle = (
    bundle: unknown
): bundle is GoalBundle => {
    if (
        typeof bundle === 'object' &&
        bundle !== null &&
        (bundle as any).resourceType === 'Bundle' &&
        typeof (bundle as any).total === 'number'
    ) {
        const entries = (bundle as any).entry
        if ((bundle as any).total === 0) {
            return true
        }
        if (Array.isArray(entries) && entries.length > 0) {
            const firstEntry = entries[0]
            return firstEntry?.resource?.resourceType === 'Goal'
        }
    }

    return false
}
